import { ReactNode, useState } from 'react';
import { Calendar, Download, File, Folder, Repeat, ShoppingBag, User, Users } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { assetUrl } from '../../../Shared/helpers';
import { useLocale } from '../../../Shared/locale';
import { Modal } from '../../../Shared/Modal/Modal';
import { Campaign, CampaignType, Pagination, Payment, PaymentStatus } from '../../../Shared/types';
import { AButton, Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Checkbox } from '../../../Shared/UI/Checkbox';
import CheckboxLabel from '../../../Shared/UI/CheckboxLabel';
import { ColorBadge } from '../../../Shared/UI/ColorBadge';
import { DateFilterInput } from '../../../Shared/UI/DateFilterInput';
import { Icon } from '../../../Shared/UI/Icon';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import { Pager } from '../../../Shared/UI/Pager';
import Picker, { PickerItem } from '../../../Shared/UI/Picker';
import { Radio } from '../../../Shared/UI/Radio';
import RadioLabel from '../../../Shared/UI/RadioLabel';
import { SearchFilterInput } from '../../../Shared/UI/SearchFilterInput';
import { useRouter } from '../../router';
import { Donation, DonationForm, SharedProps, Subscription } from '../../types';
import Container from '../../UI/Container';
import Header from '../../UI/Header';
import HeaderTitle from '../../UI/HeaderTitle';
import Layout from '../../UI/Layout';
import { DonationsLayout } from '../DonationsLayout';
import PaymentStatusLabel from '../PaymentStatusLabel';

export interface DonationsPageProps extends SharedProps {
  donations: Pagination<Donation & {
    campaign: Campaign | null;
    donation_form: DonationForm | null;
    subscription: Subscription | null;
    payment: Payment;
    email?: string | null;
    payment_status: PaymentStatus;
  }>;
  projects: PickerItem[];
  events: PickerItem[];
  campaigns: PickerItem[];
  products: PickerItem[];
  donationForms: PickerItem[];
  exportUrl: string;
}

export default function DonationsPage({
  donations,
  projects,
  events,
  campaigns,
  products,
  donationForms,
  exportUrl,
  charity,
  params,
}: DonationsPageProps) {
  const { t } = useTranslation();
  const { formatCurrency, formatDate, formatNumber } = useLocale();
  const { routes } = useRouter();

  const showFilters = !!params.search || !!params.project_id || !!params.event_id || !!params.campaign_id
    || !!params.product_id || !!params.donation_form_id || !!params.created_after || !!params.created_until
    || donations.meta.total > 0;

  const containerSize = showFilters ? 'lg' : 'md';

  const [showExportModal, setShowExportModal] = useState(false);

  return (
    <>
      <div>
        <Header>
          <Container size={containerSize}>
            <HeaderTitle>
              {t('backend:donations')}
            </HeaderTitle>
          </Container>
        </Header>

        <Container size={containerSize} className="space-y-6">
          {showFilters && (
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-3 space-y-6">
                <div className="space-y-4">
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="search">
                      {t('shared:filter.search')}
                    </InputLabel>
                    <SearchFilterInput placeholder="" id="search" />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="project">
                      {t('backend:project')}
                    </InputLabel>
                    <Picker
                      name="project_id"
                      items={projects}
                      selected={typeof params.project_id === 'string' ? params.project_id : null}
                      icon={<Folder />}
                      optional
                      payload={() => ({ campaign_id: undefined, page: undefined })}
                      id="project"
                      disabled={!params.project_id && !!params.donation_form_id}
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="event">
                      {t('backend:event')}
                    </InputLabel>
                    <Picker
                      name="event_id"
                      items={events}
                      selected={typeof params.event_id === 'string' ? params.event_id : null}
                      icon={<Calendar />}
                      optional
                      payload={() => ({ campaign_id: undefined, page: undefined })}
                      id="event"
                      disabled={!params.event_id && !!params.donation_form_id}
                    />
                  </InputGroup>
                  {campaigns.length > 0 && (
                    <InputGroup>
                      <InputLabel className="text-sm font-medium text-slate-500" htmlFor="campaign">
                        {t('backend:campaign')}
                      </InputLabel>
                      <Picker
                        name="campaign_id"
                        items={campaigns}
                        selected={typeof params.campaign_id === 'string' ? params.campaign_id : null}
                        icon={<Users />}
                        optional
                        payload={() => ({ page: undefined })}
                        id="campaign"
                        disabled={!params.campaign_id && !!params.donation_form_id}
                      />
                    </InputGroup>
                  )}
                  {products.length > 0 && (
                    <InputGroup>
                      <InputLabel className="text-sm font-medium text-slate-500" htmlFor="product">
                        {t('backend:product')}
                      </InputLabel>
                      <Picker
                        name="product_id"
                        items={products}
                        selected={typeof params.product_id === 'string' ? params.product_id : null}
                        icon={<ShoppingBag />}
                        optional
                        payload={() => ({ page: undefined })}
                        id="product"
                        disabled={!params.product_id && !!params.donation_form_id}
                      />
                    </InputGroup>
                  )}
                  {donationForms.length > 0 && (
                    <InputGroup>
                      <InputLabel className="text-sm font-medium text-slate-500" htmlFor="donation_form">
                        {t('backend:donation_form')}
                      </InputLabel>
                      <Picker
                        name="donation_form_id"
                        items={donationForms}
                        selected={typeof params.donation_form_id === 'string' ? params.donation_form_id : null}
                        icon={<File />}
                        optional
                        payload={() => ({ page: undefined })}
                        id="donation_form"
                        disabled={!!params.project_id || !!params.campaign_id || !!params.product_id}
                      />
                    </InputGroup>
                  )}
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="created_after">
                      {t('backend:from_date')}
                    </InputLabel>
                    <DateFilterInput
                      name="created_after"
                      selectsStart
                      endDate={typeof params.created_until === 'string' ? params.created_until : undefined}
                      id="created_after"
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="created_until">
                      {t('backend:until_date')}
                    </InputLabel>
                    <DateFilterInput
                      name="created_until"
                      selectsEnd
                      startDate={typeof params.created_after === 'string' ? params.created_after : undefined}
                      id="created_until"
                    />
                  </InputGroup>
                </div>
                <div>
                  <Button
                    onClick={() => setShowExportModal(true)}
                    variant="secondary"
                    size="sm"
                    disabled={donations.meta.total === 0 || donations.meta.total > 100_000}
                  >
                    <Icon className="mr-1">
                      <Download />
                    </Icon>
                    {t('backend:export_selection')}
                    {' '}
                    ({formatNumber(donations.meta.total)})
                  </Button>
                </div>
              </div>
              <div className="col-span-9 space-y-6">
                {donations.data.length > 0 && (
                  <div className="space-y-2">
                    {donations.data.map((donation) => (
                      <Card
                        key={donation.id}
                        spacing="xs"
                        link={{
                          href: routes.donation_page(donation.id),
                          ariaLabel: donation.name || donation.email || t('backend:anonymous'),
                        }}
                      >
                        <div className="grid grid-cols-[32px_4fr_4fr_2fr_2fr_2fr] gap-4 items-center leading-tight">
                          <div>
                            <ColorBadge
                              color={charity.brand_color}
                              name={donation.name || donation.email || t('backend:anonymous')}
                            />
                          </div>
                          <div className="min-w-0">
                            <h4 className="overflow-hidden leading-tight whitespace-nowrap text-ellipsis">
                              {donation.name || donation.email || t('backend:anonymous')}
                            </h4>
                          </div>
                          <div className="min-w-0 pr-6 text-sm">
                            {donation.campaign && (
                              <div className="overflow-hidden whitespace-nowrap text-ellipsis">
                                <Icon className="mr-1">
                                  {donation.campaign.type === CampaignType.Individual ? <User /> : <Users/>}
                                </Icon>
                                {donation.campaign.title}
                              </div>
                            )}
                            {donation.donation_form && (
                              <div className="overflow-hidden whitespace-nowrap text-ellipsis">
                                <Icon className="mr-1">
                                  <File />
                                </Icon>
                                {donation.donation_form.internal_title}
                              </div>
                            )}
                          </div>
                          <div className="text-sm font-medium">
                            {formatCurrency(donation.amount)}
                            {donation.subscription && (
                              <Icon className="ml-1">
                                <Repeat />
                              </Icon>
                            )}
                          </div>
                          <div className="text-sm text-slate-500">
                            {formatDate(donation.created_at)}
                          </div>
                          <div className="text-sm text-right">
                            <PaymentStatusLabel payment={donation.payment} />
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}

                {donations.meta.last_page > 1 && (
                  <Pager data={donations} />
                )}

                {showFilters && donations.meta.total === 0 && (
                  <Card className="py-24 text-center text-slate-500">
                    <img
                      src={assetUrl('images/no-results.svg')}
                      alt={t('backend:no_results')}
                      width={120}
                      className="block mx-auto mb-6"
                    />
                    {t('backend:no_results')}
                  </Card>
                )}
              </div>
            </div>
          )}

          {!showFilters && (
            <Card className="text-center text-slate-500">
              <h2>
                {t('backend:no_donations_yet')}
              </h2>
              {t('backend:no_donations_yet_description')}
            </Card>
          )}
        </Container>
      </div>

      <Modal isOpen={showExportModal} onClose={() => setShowExportModal(false)} size="sm">
        <ExportDonationsModal count={donations.meta.total} exportUrl={exportUrl} onExport={() => setShowExportModal(false)} />
      </Modal>
    </>
  );
}

DonationsPage.layout = (page: ReactNode) => <Layout><DonationsLayout>{page}</DonationsLayout></Layout>;

const ExportDonationsModal = ({
  count,
  exportUrl,
  onExport,
}: {
  count: number;
  exportUrl: string;
  onExport: () => void;
}) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();

  const [splitLastNames, setSplitLastNames] = useState(false);
  const [format, setFormat] = useState('excel');

  const getExportUrl = () => {
    const url = new URL(exportUrl);

    url.searchParams.append('export_format', format);

    if (splitLastNames) {
      url.searchParams.append('split_last_names', '1');
    }

    return url.toString();
  };

  return (
    <div className="space-y-6">
      <h3 className="py-1">
        {t('backend:export_donations', { total: formatNumber(count) })}
      </h3>

      <hr />

      <InputGroup>
        <InputLabel>
          {t('backend:file_format')}
        </InputLabel>
        <RadioLabel>
          <Radio
            checked={format === 'excel'}
            onChange={() => setFormat('excel')}
          />
          <span>
            {t('backend:excel')}
          </span>
        </RadioLabel>
        <RadioLabel>
          <Radio
            checked={format === 'csv'}
            onChange={() => setFormat('csv')}
          />
          <span>
            {t('backend:csv')}
          </span>
        </RadioLabel>
      </InputGroup>

      <hr />

      <InputGroup>
        <CheckboxLabel htmlFor="export_split_last_names">
          <Checkbox
            onChange={() => setSplitLastNames((split) => !split)}
            checked={splitLastNames}
            className="mt-1"
            id="export_split_last_names"
          />
          <span>
            {t('backend:split_last_names_label')}
          </span>
        </CheckboxLabel>
      </InputGroup>

      <hr />

      <div className="text-right">
        <AButton
          href={getExportUrl()}
          onClick={() => onExport()}
        >
          {t('backend:export')}
        </AButton>
      </div>
    </div>
  );
};
